import React, { useCallback, useEffect, useState } from 'react';

// TV buttons for our custom players.
//  - AirPlay: Safari only (native HLS / MP4 both AirPlay fine).
//  - Chromecast: Chrome only, via Google's Cast sender SDK (loaded lazily, and
//    only when the button could appear). Casts the Mux stream when we have
//    one, else the MP4.
// Each button renders nothing when its feature isn't available.

const CAST_SDK = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
let castReady = null;

const isChrome = () =>
  typeof window !== 'undefined' && !!window.chrome && /Chrome\//.test(navigator.userAgent) && !/Edg\/|OPR\//.test(navigator.userAgent);

function loadCastFramework() {
  if (castReady) return castReady;
  castReady = new Promise((resolve) => {
    if (window.cast?.framework) {
      resolve(true);
      return;
    }
    window.__onGCastApiAvailable = (available) => resolve(Boolean(available && window.cast?.framework));
    const script = document.createElement('script');
    script.src = CAST_SDK;
    script.async = true;
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
    setTimeout(() => resolve(Boolean(window.cast?.framework)), 8000);
  }).then((ok) => {
    if (!ok) return false;
    try {
      window.cast.framework.CastContext.getInstance().setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
      return true;
    } catch (_) {
      return false;
    }
  });
  return castReady;
}

const TvIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16V6a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-6" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 20a1 1 0 011 1M3 17a4 4 0 014 4M3 14a7 7 0 017 7" />
  </svg>
);

const AirPlayIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 17H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v9a2 2 0 01-2 2h-1" />
    <path strokeLinejoin="round" d="M12 14l5 6H7l5-6z" fill="currentColor" />
  </svg>
);

export function AirPlayButton({ videoRef, className, iconClassName = 'w-5 h-5' }) {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof window === 'undefined' || !window.WebKitPlaybackTargetAvailabilityEvent) return undefined;
    const onChange = (e) => setAvailable(e.availability === 'available');
    video.addEventListener('webkitplaybacktargetavailabilitychanged', onChange);
    return () => video.removeEventListener('webkitplaybacktargetavailabilitychanged', onChange);
  }, [videoRef]);

  if (!available) return null;
  return (
    <button
      type="button"
      className={className}
      aria-label="Play on Apple TV"
      title="AirPlay"
      onClick={(e) => {
        e.stopPropagation();
        videoRef.current?.webkitShowPlaybackTargetPicker?.();
      }}
    >
      <AirPlayIcon className={iconClassName} />
    </button>
  );
}

export function ChromecastButton({ videoRef, source, title, poster, className, iconClassName = 'w-5 h-5' }) {
  const [state, setState] = useState('NO_DEVICES_AVAILABLE');
  const [ready, setReady] = useState(false);
  const [engaged, setEngaged] = useState(false);

  // Google's Cast script only loads once the viewer actually plays something,
  // not on every page that shows a player.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || engaged) return undefined;
    if (!video.paused) {
      setEngaged(true);
      return undefined;
    }
    const onPlay = () => setEngaged(true);
    video.addEventListener('play', onPlay);
    return () => video.removeEventListener('play', onPlay);
  }, [videoRef, engaged]);

  useEffect(() => {
    if (!engaged || !isChrome() || !source) return undefined;
    let context = null;
    let listener = null;
    let cancelled = false;
    loadCastFramework().then((ok) => {
      if (!ok || cancelled) return;
      context = window.cast.framework.CastContext.getInstance();
      listener = (e) => setState(e.castState);
      context.addEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, listener);
      setState(context.getCastState());
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (context && listener) {
        context.removeEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, listener);
      }
    };
  }, [source, engaged]);

  const cast = useCallback(async (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video || !source) return;
    const { cast: castNs, chrome } = window;
    const context = castNs.framework.CastContext.getInstance();
    try {
      if (!context.getCurrentSession()) await context.requestSession();
      const session = context.getCurrentSession();
      if (!session) return;
      const isHls = source.includes('.m3u8');
      const info = new chrome.cast.media.MediaInfo(source, isHls ? 'application/x-mpegurl' : 'video/mp4');
      info.streamType = chrome.cast.media.StreamType.BUFFERED;
      const meta = new chrome.cast.media.GenericMediaMetadata();
      meta.title = title || 'Pixley story';
      if (poster) meta.images = [new chrome.cast.Image(poster)];
      info.metadata = meta;
      const request = new chrome.cast.media.LoadRequest(info);
      request.currentTime = video.currentTime || 0;
      request.autoplay = true;
      await session.loadMedia(request);
      video.pause();
    } catch (_) {
      // Picker dismissed or device refused: nothing to undo.
    }
  }, [videoRef, source, title, poster]);

  if (!ready || state === 'NO_DEVICES_AVAILABLE') return null;
  const connected = state === 'CONNECTED';
  return (
    <button
      type="button"
      className={className}
      aria-label={connected ? 'Playing on TV' : 'Play on TV'}
      title={connected ? 'Playing on TV' : 'Cast to TV'}
      onClick={cast}
      style={connected ? { color: '#0065F8' } : undefined}
    >
      <TvIcon className={iconClassName} />
    </button>
  );
}
